/**
 * Utility functions for time formatting
 */

/**
 * Format Unix timestamp (in seconds) to EST time string
 * @param timestamp Unix timestamp in seconds
 * @returns Formatted EST time string like "08 Oct 2025 19:59"
 */
export function formatTimestampToEST(timestamp: number): string {
  const date = new Date(timestamp * 1000); // Convert to milliseconds

  // Convert to EST timezone
  const estDate = new Date(date.toLocaleString("en-US", { timeZone: "America/New_York" }));

  // Format as "08 Oct 2025 19:59"
  const day = estDate.getDate().toString().padStart(2, '0');
  const month = estDate.toLocaleString('en-US', { month: 'short' });
  const year = estDate.getFullYear();
  const hours = estDate.getHours().toString().padStart(2, '0');
  const minutes = estDate.getMinutes().toString().padStart(2, '0');

  return `${day} ${month} ${year} ${hours}:${minutes}`;
}

/**
 * Format Unix timestamp (in seconds) to EST time string with seconds
 * @param timestamp Unix timestamp in seconds
 * @returns Formatted EST time string like "08 Oct 2025 19:59:30"
 */
export function formatTimestampToESTWithSeconds(timestamp: number): string {
  const date = new Date(timestamp * 1000); // Convert to milliseconds

  // Convert to EST timezone
  const estDate = new Date(date.toLocaleString("en-US", { timeZone: "America/New_York" }));

  // Format as "08 Oct 2025 19:59:30"
  const day = estDate.getDate().toString().padStart(2, '0');
  const month = estDate.toLocaleString('en-US', { month: 'short' });
  const year = estDate.getFullYear();
  const hours = estDate.getHours().toString().padStart(2, '0');
  const minutes = estDate.getMinutes().toString().padStart(2, '0');
  const seconds = estDate.getSeconds().toString().padStart(2, '0');

  return `${day} ${month} ${year} ${hours}:${minutes}:${seconds}`;
}

/**
 * Get current EST time string
 * @returns Current EST time formatted as "08 Oct 2025 19:59"
 */
export function getCurrentESTTime(): string {
  return formatTimestampToEST(Math.floor(Date.now() / 1000));
}

/**
 * Legacy function for backward compatibility
 * @param timestamp Unix timestamp in seconds (string or number)
 * @returns Formatted EST time string like "08 Oct 2025 19:59"
 */
export function formatTimestamp(timestamp: string | number): string {
  const numericTimestamp = typeof timestamp === 'string' ? parseFloat(timestamp) : timestamp;
  return formatTimestampToEST(numericTimestamp);
}